/**
 * Selling Plan Media Filter
 *
 * Filters the product media gallery based on the active selling plan type.
 * Media items with alt text containing #one-time or #subscription will be
 * shown/hidden when the selling plan changes.
 *
 * Works alongside the existing variant-based media filtering (is-filtered)
 * by using a separate class (is-sp-filtered) so both filters are independent.
 *
 * Usage: Add #one-time or #subscription to media alt text in Shopify admin.
 *   e.g. "Product hero image #one-time"
 *   e.g. "Subscription bundle shot #subscription"
 *   Media without these tags are always visible (subject to variant filtering).
 */

(function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // Configuration — change these values to adjust behavior
  // ---------------------------------------------------------------------------

  /** Custom event name dispatched when the selling plan type changes */
  const SELLING_PLAN_CHANGE_EVENT = "selling-plan:changed";

  /** data attribute on media items that stores their selling plan visibility */
  const SP_VISIBILITY_ATTR = "data-sp-visibility";

  /** CSS class toggled on items that should be hidden by selling plan filter */
  const SP_FILTERED_CLASS = "is-sp-filtered";

  /** Value for one-time purchase selling plan type */
  const PLAN_TYPE_ONE_TIME = "one-time";

  /** Value for subscription selling plan type */
  const PLAN_TYPE_SUBSCRIPTION = "subscription";

  /** Value indicating media is visible for all selling plan types */
  const PLAN_TYPE_ALL = "all";

  // ---------------------------------------------------------------------------
  // Stubbed event emitter — replace this with the real implementation when the
  // selling plan picker dispatches actual events.
  // ---------------------------------------------------------------------------

  /**
   * Dispatches a selling plan change event on the document.
   *
   * @param {string} planType - One of PLAN_TYPE_ONE_TIME or PLAN_TYPE_SUBSCRIPTION
   *
   * Call this from wherever the selling plan toggle lives, e.g.:
   *   dispatchSellingPlanChange("subscription");
   *   dispatchSellingPlanChange("one-time");
   */
  function dispatchSellingPlanChange(planType) {
    document.dispatchEvent(
      new CustomEvent(SELLING_PLAN_CHANGE_EVENT, {
        bubbles: true,
        detail: { planType: planType },
      })
    );
  }

  // Expose the dispatcher globally so other scripts can call it
  window.dispatchSellingPlanChange = dispatchSellingPlanChange;

  // ---------------------------------------------------------------------------
  // Core filtering logic
  // ---------------------------------------------------------------------------

  /**
   * Filters all media elements inside a product-media component based on the
   * active selling plan type. Elements whose data-sp-visibility does not match
   * the active plan type get the is-sp-filtered class; matching elements (and
   * those with visibility "all") have the class removed.
   *
   * After toggling classes, the Flickity carousel is reloaded so it picks up
   * the new set of visible slides.
   *
   * @param {HTMLElement} productMediaEl - The <product-media> custom element
   * @param {string} activePlanType - The active selling plan type
   */
  function filterMediaBySellingPlan(productMediaEl, activePlanType) {
    const allItems = productMediaEl.querySelectorAll("[" + SP_VISIBILITY_ATTR + "]");
    let changed = false;

    allItems.forEach(function (item) {
      const visibility = item.getAttribute(SP_VISIBILITY_ATTR);
      const shouldFilter =
        visibility !== PLAN_TYPE_ALL && visibility !== activePlanType;
      const isCurrentlyFiltered = item.classList.contains(SP_FILTERED_CLASS);

      if (shouldFilter !== isCurrentlyFiltered) {
        item.classList.toggle(SP_FILTERED_CLASS, shouldFilter);
        changed = true;
      }
    });

    if (changed) {
      const carousel = productMediaEl.querySelector("flickity-carousel");
      if (carousel && typeof carousel.reload === "function") {
        var visibleSelector = ".product__media-item:not(.is-filtered):not(." + SP_FILTERED_CLASS + ")";

        // Keep the currently selected slide if it's still visible
        var currentSelected = productMediaEl.querySelector(
          ".product__media-item.is-selected:not(.is-filtered):not(." + SP_FILTERED_CLASS + ")"
        );

        // Only re-select if the current slide got filtered out
        if (!currentSelected) {
          var firstVisible = productMediaEl.querySelector(visibleSelector);
          if (firstVisible) {
            productMediaEl
              .querySelectorAll(".product__media-item.is-selected, .product__media-item.is-initial-selected")
              .forEach(function (el) {
                el.classList.remove("is-selected", "is-initial-selected");
              });
            firstVisible.classList.add("is-selected", "is-initial-selected");
          }
        }
        carousel.reload();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Event listener — responds to selling plan changes
  // ---------------------------------------------------------------------------

  document.addEventListener(SELLING_PLAN_CHANGE_EVENT, function (event) {
    var planType = event.detail && event.detail.planType;
    if (!planType) return;

    var productMediaElements = document.querySelectorAll("product-media");
    productMediaElements.forEach(function (el) {
      filterMediaBySellingPlan(el, planType);
    });
  });
})();
